import { Pagination, Card, Divider, Button, message } from 'antd';
import { useEffect, useState, useRef } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { CSVLink } from 'react-csv';
import moment from 'moment';
import styled from 'styled-components';
import { DownloadOutlined } from '@ant-design/icons';
import SearchFilter from '../../component/Filter';
import baseuri from '../../methods/baseuri';
import { commas } from '../../methods/utils/general-utils';
import { useAppDispatch, useAppSelector } from '../../methods/redux/hooks';
import { getCurrencies } from '../../methods/redux/reducers/currency';
import { getUserProfileById } from '../../methods/redux/reducers/user-profile';
import remakeQueryString from '../../methods/utils/remake-query-string';
import {
	investmentTopUpList,
	investmentTopUpStatusCount,
} from '../../methods/redux/reducers/investment';
import InvestmentTopUpItems from './InvestmentTopUpItems';
import InvestmentTopUpStatusCount from './InvestmentTopUpStatusCount';
import NativeNumber from '../../component/Numeral';

const DownloadButton = styled(Button)`
	color: red;
	background-color: #f5d5d5;
	border-radius: 5px;
	&:hover {
		color: red;
		background-color: #efe7e7;
	}
	&:focus {
		color: red;
		background-color: #f5d5d5;
	}
`;

const TopupInvestmentListPage = () => {
	const dispatch = useAppDispatch();
	const { userId } = useParams();
	const [searchParams, setSearchParams] = useSearchParams();
	const [loading, setLoading] = useState(false);
	const [results, setResults] = useState([] as any);

	const { currencies } = useAppSelector((store) => store.currencies);
	const { investmentTopUps, investmentTopUpStatus } = useAppSelector(
		(store) => store.investment
	);
	const { user } = useAppSelector((store) => store.userProfile);
	const searchParamsObject = Object.fromEntries(searchParams);
	const InvestmentId = searchParamsObject.investmentId;
	const getQueryParams = () => Object.fromEntries(searchParams.entries());
	const csvLinkRef = useRef<
		CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }
	>(null);

	useEffect(() => {
		const queryData = Object.fromEntries(searchParams.entries());
		if (Object.keys(queryData).length === 0) {
			setSearchParams({
				status: 'active',
				pageId: '1',
				perPage: String(investmentTopUps.topUps.paginator.perPage),
			});
		}

		if (userId) {
			dispatch(getUserProfileById(userId));
		}

		dispatch(getCurrencies({}));
	}, []);

	const updateQueryString = (query: any, action = 'update') => {
		remakeQueryString(query, searchParams, setSearchParams, action);
	};

	useEffect(() => {
		const queryData = Object.fromEntries(searchParams.entries());
		if (Object.keys(queryData).length > 0) {
			if (InvestmentId) {
				dispatch(
					investmentTopUpList({ ...queryData, investmentId: InvestmentId })
				);
				dispatch(
					investmentTopUpStatusCount({
						...queryData,
						investmentId: InvestmentId,
					})
				);
			} else {
				dispatch(investmentTopUpList({ ...queryData }));
				dispatch(investmentTopUpStatusCount({ ...queryData }));
			}
		}
	}, [searchParams]);

	useEffect(() => {
		if (userId) {
			dispatch(getUserProfileById(userId));
		}
	}, [userId]);

	const currencySelect = currencies.map((item: any) => ({
		name: item.symbol,
		value: item._id,
	}));

	// Transform the results data to match the headers
	const transformDownloadRequests = (requests: []) =>
		requests?.map((data: any) => ({
			title: data?.investmentId?.title,
			id: data?._id,
			customer: data.userId?.username,
			investmentForm: data.investmentForm,
			investmentId: data?.investmentId._id, // Handle nested value
			investmentFatherCreditId: data.investmentFatherCreditId || '--', // Handle computed value
			proxyStaffId: data.proxyStaffId,
			status: data.status,
			transactionStatus: data.transactionStatus,
			amount: data.amount,
			discount: `${commas(data?.calculatedFee?.discount)} ${
				data.currencyId?.symbol
			}`,
			fee_usd: `${commas(data?.calculatedFee?.feeInUsd)} ${
				data.currencyId?.symbol
			}`,
			isMigrated: data?.isMigrated ? 'True' : 'False',
			createdAt: moment(data.createdAt).format('lll'), // Handle formatted date
		}));

	// Define headers with simple keys
	const headers = [
		{ label: 'Title', key: 'title' },
		{ label: 'Id', key: 'id' },
		{ label: 'Customer', key: 'customer' },
		{ label: 'Investment Form', key: 'investmentForm' },
		{ label: 'Investment Id', key: 'investmentId' },
		{ label: 'Investment Father Credit Id', key: 'investmentFatherCreditId' },
		{ label: 'Proxy Staff Id', key: 'proxyStaffId' },
		{ label: 'Status', key: 'status' },
		{ label: 'Transaction Status', key: 'transactionStatus' },
		{ label: 'Amount', key: 'amount' },
		{ label: 'Discount', key: 'discount' },
		{ label: 'Fee in USD', key: 'fee_usd' },
		{ label: 'Is Migrated', key: 'isMigrated' },
		{ label: 'Created At', key: 'createdAt' },
	];

	const handleDownload = () => {
		setLoading(true);
		const params1 = getQueryParams();

		setResults([]);
		baseuri
			.get('/admin/investments/top-ups' || '', {
				params: {
					...params1,
					perPage: investmentTopUps?.topUps?.paginator?.itemCount || 0,
					pageId: '1',
				},
			})
			.then((res: any) => {
				const rawData = res?.data?.data?.topUps?.itemsList;
				const transformedData = transformDownloadRequests(rawData);
				setResults(transformedData);
				setLoading(false);
			})
			.catch(() => {
				message.error('could not download CSV');
				setLoading(false);
			});
	};

	useEffect(() => {
		if (results.length > 0) {
			csvLinkRef?.current?.link.click();
		}
	}, [results]);

	return (
		<div>
			<Card>
				<Button
					onClick={() => window.history.back()}
					style={{ padding: '10px', cursor: 'pointer' }}
				>
					Back
				</Button>
				<h1>
					Investment Topup Items{' '}
					{userId && (
						<span>
							for{' '}
							<Link to={`/user-profile/${user.profile?._id}`}>
								@{user.profile?.username}
							</Link>
						</span>
					)}
				</h1>
				<br />

				<h2>
					Total Amount:{' '}
					<span style={{ color: 'var(--green-color)' }}>
						${' '}
						<NativeNumber
							value={Number(investmentTopUps.totalAmount || 0)}
							decimalPlaces={2}
						/>
					</span>
				</h2>

				<div className="mini-report">
					<InvestmentTopUpStatusCount
						statusTopupCount={investmentTopUpStatus}
					/>
				</div>
				<Divider />
				<SearchFilter
					title="Search Investment Statement Top-ups"
					searchFilters={[
						{
							label: 'Status',
							name: 'status',
							htmlElement: 'select',
							selectOptions: [
								{ name: 'Pending', value: 'pending' },
								{ name: 'Active', value: 'active' },
								{ name: 'Completed', value: 'completed' },
								{ name: 'Terminated', value: 'terminated' },
								{ name: 'Deleted', value: 'deleted' },
							],
						},
						{
							label: 'InvestmentForm',
							name: 'investmentForm',
							htmlElement: 'select',
							selectOptions: [
								{ name: 'STOCKS', value: 'STOCKS' },
								{ name: 'REAL_ESTATE', value: 'REAL_ESTATE' },
							],
						},
						{
							label: 'TransactionStatus',
							name: 'transactionStatus',
							htmlElement: 'select',
							selectOptions: [
								{ name: 'pending', value: 'pending' },
								{ name: 'successful', value: 'successful' },
								{ name: 'completed', value: 'completed' },
								{ name: 'cancelled', value: 'cancelled' },
							],
						},
						{
							label: 'Currency',
							name: 'currencyId',
							htmlElement: 'select',
							selectOptions: currencySelect,
						},
						{
							label: 'isMigrated',
							name: 'isMigrated',
							htmlElement: 'select',
							selectOptions: [
								{ name: 'True', value: true },
								{ name: 'False', value: false },
							],
						},
					]}
					retrieveQueryString={updateQueryString}
				/>
				<br />
				<p>
					Total of{' '}
					<strong>{investmentTopUps?.topUps?.paginator?.itemCount}</strong>{' '}
					found.
				</p>
				<Divider />
				<div>
					<Pagination
						onChange={(pageId) => updateQueryString({ pageId })}
						total={investmentTopUps?.topUps?.paginator?.itemCount}
						current={investmentTopUps?.topUps?.paginator?.currentPage}
						pageSize={investmentTopUps?.topUps?.paginator?.perPage ?? 20}
					/>
				</div>
				{investmentTopUps?.topUps?.itemsList?.length > 0 && (
					<div style={{ display: 'flex', justifyContent: 'flex-end' }}>
						<DownloadButton
							danger
							loading={loading}
							icon={<DownloadOutlined />}
							onClick={() => handleDownload()}
						>
							{loading ? 'Loading csv...' : 'Download CSV'}
						</DownloadButton>
						<CSVLink
							data={results}
							headers={headers}
							className="download"
							filename="InvestmentTopup.csv"
							ref={csvLinkRef}
						/>
					</div>
				)}
				<br />
				{investmentTopUps?.topUps?.itemsList?.map((item: any) => (
					<InvestmentTopUpItems key={item._id} item={item} />
				))}
				<br />
				<Pagination
					onChange={(pageId) => updateQueryString({ pageId })}
					total={investmentTopUps?.topUps?.paginator?.itemCount}
					current={investmentTopUps?.topUps?.paginator?.currentPage}
					pageSize={investmentTopUps?.topUps?.paginator?.perPage ?? 20}
					showSizeChanger
					showQuickJumper
				/>
			</Card>
		</div>
	);
};

export default TopupInvestmentListPage;
